import { Injectable, inject } from '@angular/core';
import { AlertProviderIcons } from '@app/modules/cop/store/cop/models/alert-provider-icons.model';
import { Layer } from '@app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetSubtype, Document } from '@app/modules/core/store/models';
import { DatafeedProviders } from '@app/modules/management/store/datafeeds/datafeed-provider.model';
import { IconProperties, IconTypes } from '@app/shared/components/icon';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

interface AlertUtilParams {
  dataset: Dataset;
  document: Document;
  layer: Layer;
}

@Injectable({ providedIn: 'root' })
export class AlertUtilsService {
  // -- dependencies --
  private translate = inject(TranslateService);

  // -- variables --
  private providerIconKeys = Object.keys(AlertProviderIcons);

  // -- methods --
  private getProviderIconURL(provider: string): string | undefined {
    if (!provider) {
      return;
    }

    provider = provider.toLowerCase();

    const providerKey = this.providerIconKeys.find(key => provider.startsWith(key));

    if (!providerKey) {
      return;
    }

    return AlertProviderIcons[providerKey];
  }

  public getIconProperties({ document, layer, dataset: { subtype } }: AlertUtilParams): IconProperties | undefined {
    const providerIconURL = this.getProviderIconURL(document.provider);

    switch (subtype) {
      case DatasetSubtype.Intelligence:
        return !!providerIconURL
          ? { type: IconTypes.URL, path: providerIconURL }
          : { type: IconTypes.SystemIcon, path: 'icon-alert_status' };

      case DatasetSubtype.Weather:
        return {
          type: IconTypes.SystemIcon,
          path: 'icon-small_solid_severe-weather',
          style: { backgroundColor: layer?.color ?? '#ffffff', color: '#ffffff', borderRadius: '50%' }
        };

      case DatasetSubtype.TropicalCyclone:
        return { type: IconTypes.URL, path: AlertProviderIcons['topoone tropical-cyclones'] };

      case DatasetSubtype.Wildfire:
        return { type: IconTypes.URL, path: '/api/v1/meta/markers/wildfire/1FA1FF/0/0/normal/wildfire.png' };

      case DatasetSubtype.Earthquake:
        return { type: IconTypes.URL, path: !!providerIconURL ? providerIconURL : AlertProviderIcons['topoone earthquakes'] };

      case DatasetSubtype.PowerOutage:
      case DatasetSubtype.PowerOutageV2:
        return { type: IconTypes.URL, path: AlertProviderIcons['poweroutage.us'] };

      case DatasetSubtype.Flight:
        let aircraftIconName = 'aircraft-large';

        if (!!document.aircraft_description && !!document.wtc) {
          // default to commercial aircraft marker: "aircraft-large"
          if (document.aircraft_description == 'Helicopter' || document.aircraft_description == 'Gyrocopter') {
            aircraftIconName = 'aircraft-helicopter';
          }
          if (document.aircraft_description == 'LandPlane' && document.wtc == 'L') {
            aircraftIconName = 'aircraft-small';
          }
        }

        const aircraftColor = document.is_landed || document.on_ground ? 'FFFF33' : layer?.color?.replace('#', '') ?? '1FA1FF';

        return {
          type: IconTypes.URL,
          path: `/api/v1/meta/markers/${aircraftIconName}/${aircraftColor}/45/0/normal/${aircraftIconName}.png`
        };

      case DatasetSubtype.Vehicle:
        const samsaraIconColor = window.document.body.dataset.theme === 'light' ? 'dark' : 'white';

        return {
          type: IconTypes.URL,
          path: `/assets/images/samsara/samsara-logo-${samsaraIconColor}.svg`
        };

      case DatasetSubtype.HorizonLocationIntelRatings:
      case DatasetSubtype.HorizonGeospatialThreatZone:
        return {
          type: IconTypes.URL,
          path: 'https://files.topo.ai/partners/logos/Crisis24.svg'
        };

      default:
        if (!!providerIconURL) {
          return {
            type: IconTypes.URL,
            path: providerIconURL
          };
        }

        return {
          type: layer?.isCustomIcon ? IconTypes.Custom : IconTypes.MaterialIcon,
          path: layer?.iconPath,
          style: {
            backgroundColor: layer?.color ?? '#ffffff',
            color: '#ffffff',
            borderRadius: '50%'
          }
        };
    }
  }

  public getHeaderLabel({ dataset, document, layer }: AlertUtilParams): string {
    const { subtype, name: datasetName } = dataset;
    const { category, provider } = document;
    const { name: layerName } = layer;

    switch (subtype) {
      case DatasetSubtype.Earthquake:
        return category;

      case DatasetSubtype.Flight:
        return layerName;

      case DatasetSubtype.Intelligence:
        if (!category && !provider) {
          return layerName;
        }

        return !!category ? `${provider} | ${category}` : provider;

      case DatasetSubtype.PowerOutage:
        return this.translate.instant(`MANAGEMENT.DATAFEEDS.PROVIDER-TYPE-${DatafeedProviders.PowerOutage}`);

      case DatasetSubtype.PowerOutageV2:
        return this.translate.instant(`MANAGEMENT.DATAFEEDS.PROVIDER-TYPE-${DatafeedProviders.PowerOutageV2}`);

      case DatasetSubtype.Vehicle:
        return this.translate.instant('MAP.SAMSARA.ALERTS');

      case DatasetSubtype.TropicalCyclone:
        return !!provider ? provider : datasetName;

      case DatasetSubtype.Weather:
        return this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-3');

      case DatasetSubtype.Wildfire:
        return this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-5-PLURAL');

      default:
        return datasetName;
    }
  }

  public shouldDisplayImpactedAssets(subtype: DatasetSubtype): boolean {
    switch (subtype) {
      case DatasetSubtype.Vehicle:
      case DatasetSubtype.HorizonLocationIntelRatings:
      case DatasetSubtype.HorizonGeospatialThreatZone:
        return false;
      default:
        return true;
    }
  }

  public getPostedDate(document: Document, subtype: DatasetSubtype, fieldOverride?: string): moment.Moment {
    if (!!fieldOverride && !!document[fieldOverride]) {
      return moment(document[fieldOverride]);
    }

    const { date_created, posted, posted_date, published_date, timestamps_posted } = document;

    switch (subtype) {
      case DatasetSubtype.Intelligence:
        return moment(posted_date ?? posted ?? date_created);

      case DatasetSubtype.Weather:
        return moment(posted ?? timestamps_posted ?? published_date ?? date_created);

      default:
        return moment(published_date ?? posted_date ?? date_created);
    }
  }
}
